import { formatPhone } from "@/shared/format/phone";
import { HttpError, request } from "@/shared/http/client";

/**
 * Where a NON-dynamic thing is read from.
 *
 * ── THE TWO THAT STAY HAND-WRITTEN ────────────────────────────────────────
 *
 * `ref_contact` resolves to `contact` and `ref_user` to `app_user`, and neither is
 * a dynamic entity — they are core tables with their own endpoints and their own
 * rules. Both used to go to `/v3/records/contact` and `/v3/records/app_user`, both
 * answered 404, and the select silently offered nothing.
 *
 * They are still declared here, one by one, and NOT read through `/v3/catalog`,
 * because each does something the catalogue's fixed `{id, label, hint}` projection
 * cannot: a contact resolves by id (the reason a form saved months ago stopped
 * showing a uuid), falls back to a `displayName` the server composes, and hides
 * the archived; a colleague who has left is still read for their LABEL and never
 * OFFERED. Moving them would trade three fixed bugs for a tidier map.
 *
 * ── AND THE ONE THAT IS NO LONGER A MAP ───────────────────────────────────
 *
 * Everything else the platform keeps a list of — channels, funnels, stages,
 * message templates, artifacts, schedules — is `GET /v3/catalog/:source`. That is
 * the half this file stops growing: a source added to the server's whitelist
 * answers here with no release of this screen, which is what the hardcoded map
 * could never do.
 */

/** A row from a core endpoint, in the fields any of them may carry. */
export interface CoreRow {
	id: string;
	name?: string | null;
	/** A contact's fallback name, already resolved by the server. */
	displayName?: string | null;
	email?: string | null;
	phone?: string | null;
	/** Only `app_user` carries one; a contact is never "suspended". */
	status?: string | null;
}

export interface CoreSource {
	/** The URL for one query. Sources that search do it server-side; see `searchesOnServer`. */
	list(query: string, limit: number): string;
	rows(payload: never): CoreRow[];
	/** One row by id, where the endpoint has such a route. */
	one?(id: string): string;
	searchesOnServer?: boolean;
	/**
	 * Whether a row may still be CHOSEN. Rows that fail it are read for their
	 * label and never offered — a reference already saved to somebody who has
	 * since left has to keep rendering their name, or the form would show a raw
	 * uuid the moment they were deactivated.
	 */
	selectable?(row: CoreRow): boolean;
}

export const CORE_SOURCES: Record<string, CoreSource> = {
	// The envelope key differs per endpoint and is checked against what each one actually
	// returns — `/v3/workspace/team` answers `{ team }` and `/v3/contacts` answers
	// `{ items, total }`. Guessing `members` here would have left the select as empty as
	// the 404 did, and just as quietly.
	app_user: {
		// A team is tens of people and the endpoint takes no parameters, so the whole
		// list comes down once and the query is applied by the caller.
		list: () => "/v3/workspace/team",
		rows: (payload: { team?: CoreRow[] }) => payload.team ?? [],
		// Somebody who left the company must stop being OFFERED as "responsável".
		// `status` has always travelled in this envelope and nothing read it, so a
		// deactivated colleague looked exactly like an active one in the picker.
		// Not filtered server-side because the same endpoint feeds the team SCREEN,
		// which has to show the deactivated in order to reactivate them.
		selectable: (row) => row.status !== "suspended",
	},
	contact: {
		// `listContactsSchema` has taken `search` all along and this file did not send it.
		// The consequence was not slowness: the browser filtered the FIRST PAGE, so the
		// twenty-sixth contact onwards could not be found by typing their name, and
		// nothing on screen said the search had a horizon.
		list: (query, limit) =>
			`/v3/contacts?${new URLSearchParams({
				limit: String(limit),
				...(query ? { search: query } : {}),
			})}`,
		rows: (payload: { items?: CoreRow[] }) => payload.items ?? [],
		one: (id) => `/v3/contacts/${id}`,
		searchesOnServer: true,
	},
};

/**
 * A hand-written source BY NAME — and never `CORE_SOURCES[name]`.
 *
 * The name arrives from `config.entity`, which whoever draws the screen types. On a
 * plain object `"constructor"` and `"toString"` answer with something that is not an
 * entry at all, and the picker would fail with "source.list is not a function"
 * instead of saying the name means nothing. The server refuses the same trick on
 * its whitelist for the same reason (`catalog/application/use-cases/read-catalog.ts`).
 */
const CORE_NAMES = new Set(Object.keys(CORE_SOURCES));

export function coreSourceFor(name: string): CoreSource | null {
	return CORE_NAMES.has(name) ? (CORE_SOURCES[name] ?? null) : null;
}

/** One choosable row of a platform list. The server's envelope, unchanged. */
export interface CatalogItem {
	readonly id: string;
	readonly label: string;
	readonly hint: string | null;
}

/**
 * A platform list, by the name the field points at.
 *
 * The name is not a URL this file assembles from parts: it IS the source, and the
 * server refuses one it has not whitelisted before touching the database. So a
 * `config.entity` nobody declared fails loudly here instead of drawing an empty box.
 */
export async function readCatalog(
	source: string,
	query: string,
	limit: number,
): Promise<CatalogItem[]> {
	const params = new URLSearchParams({ limit: String(limit) });
	if (query) params.set("q", query);
	const payload = await request<{ items?: CatalogItem[] }>(
		// Encoded: a name with a slash in it would otherwise become a different PATH,
		// and the 404 that came back would be about a route rather than about a source.
		`/v3/catalog/${encodeURIComponent(source)}?${params.toString()}`,
	);
	return payload.items ?? [];
}

/** A missing thing, told apart from a broken one. The fallbacks turn on this. */
export function isNotFound(error: unknown): boolean {
	return error instanceof HttpError && error.kind === "notFound";
}

/**
 * COMO UMA LINHA SE CHAMA — a definição primeiro, o palpite depois.
 *
 * ── O DEFEITO DE 28/08 ────────────────────────────────────────────────────
 *
 * Isto era só a lista de chaves convencionais abaixo, e o comentário dizia que
 * qualquer coisa mais esperta precisaria de "conhecimento por entidade, que é
 * exatamente o que a camada dinâmica existe para evitar". Estava trocado: o que
 * a camada dinâmica evita é o palpite CRAVADO no código — ler a definição da
 * entidade É ser dirigido por metadado.
 *
 * Uma entidade cujo campo-título se chama `full_name` (ou `service_name`, ou
 * `razao_social`) não bate em nenhuma das sete chaves, e o rótulo caía no `id`.
 * O resultado na tela: o seletor de "Profissional" oferecia
 * `50943501-02d3-…` em vez de "mario", e a coluna da tabela mostrava o mesmo
 * uuid. O registo estava certo o tempo todo; ninguém sabia perguntar o nome.
 *
 * `preferred` é o código do campo que a ENTIDADE declara como título — o texto
 * obrigatório (`labelForRow`, `lookup-port.ts`). As chaves convencionais continuam, e continuam a ser o certo
 * para as fontes do núcleo (um contato traz `name`, um colega `displayName`),
 * que não têm `field_def` nenhum a consultar.
 */
export function labelOf(row: Record<string, unknown> | CoreRow, preferred?: string | null): string {
	const named = preferred ? (row as Record<string, unknown>)[preferred] : null;
	if (typeof named === "string" && named.trim().length > 0) return named;
	// `displayName` before the rest: a contact carries one precisely because a person
	// with no name still has to be called something on screen.
	for (const key of ["name", "displayName", "title", "label", "code", "email", "phone"]) {
		const value = (row as Record<string, unknown>)[key];
		if (typeof value === "string" && value.trim().length > 0) return shown(key, value);
	}
	return String(row.id ?? "—");
}

/**
 * The second line of a core row: what says WHICH one this is.
 *
 * A contact's phone or e-mail, a colleague's role. Never the same string as the
 * label — a row whose name IS its phone must not read "+5511… · +5511…".
 */
export function hintOf(row: Record<string, unknown> | CoreRow): string | undefined {
	const label = labelOf(row);
	for (const key of ["phone", "email", "role", "externalRef"]) {
		const value = (row as Record<string, unknown>)[key];
		if (typeof value === "string" && value.trim() && shown(key, value) !== label)
			return shown(key, value);
	}
	return undefined;
}

/** O telefone como se lê ("+55 98 43939-3443"), nunca o E.164 guardado. */
export function shown(key: string, value: string): string {
	return key === "phone" ? (formatPhone(value) ?? value) : value;
}
